import { useEffect, useState } from 'react';
import { appleMusicUrl, type PublicGuest, type PublicTrack } from '@karozik/shared';
import { api, Artwork, Avatar, formatClock, Spinner } from '@karozik/ui';
import { PhotosCard } from '../components/Photos';

interface RecapData {
  event: { name: string };
  tracksPlayed: number;
  guestTracksPlayed: number;
  totalReactions: number;
  topEmoji: string | null;
  guests: number;
  mostVoted: { track: PublicTrack | null; votes: number; proposer: PublicGuest | null } | null;
  badges: { id: string; emoji: string; title: string; description: string; guest: PublicGuest | null }[];
  playlist: { track: PublicTrack | null; startedAt: number; proposer: PublicGuest | null; dedication: string | null }[];
}

export function Recap({ token }: { token: string }) {
  const [data, setData] = useState<RecapData | null>(null);
  useEffect(() => {
    void api<RecapData>(`/api/join/${token}/recap`).then(setData);
  }, [token]);
  if (!data) {
    return (
      <div className="flex min-h-dvh items-center justify-center">
        <Spinner />
      </div>
    );
  }
  return (
    <main className="mx-auto max-w-md px-5 pt-10 pb-16">
      <p className="text-sm font-semibold tracking-widest text-pink uppercase">C’était</p>
      <h1 className="font-display text-3xl font-bold">{data.event.name}</h1>
      <p className="mt-2 text-ink-dim">Merci d’avoir fait la musique ce soir 💜</p>

      <div className="mt-6 grid grid-cols-3 gap-2 text-center">
        <Stat value={data.tracksPlayed} label="titres joués" />
        <Stat value={data.guestTracksPlayed} label="proposés par vous" />
        <Stat value={data.totalReactions} label={`réactions ${data.topEmoji ?? ''}`} />
      </div>

      <PhotosCard joinToken={token} className="mt-8" />

      {data.badges.length > 0 && (
        <section className="mt-8">
          <h2 className="font-display mb-3 text-lg font-bold">Les trophées</h2>
          <ul className="space-y-2">
            {data.badges.map((b) => (
              <li key={b.id} className="flex items-center gap-3 rounded-2xl bg-surface p-3">
                <span className="text-3xl">{b.emoji}</span>
                <div className="min-w-0 flex-1">
                  <div className="font-semibold">{b.title}</div>
                  <div className="text-sm text-ink-dim">{b.description}</div>
                </div>
                {b.guest && (
                  <div className="flex items-center gap-2">
                    <Avatar guest={b.guest} size="sm" />
                    <span className="font-semibold">{b.guest.pseudo}</span>
                  </div>
                )}
              </li>
            ))}
          </ul>
        </section>
      )}

      <section className="mt-8">
        <h2 className="font-display mb-3 text-lg font-bold">La playlist de la soirée</h2>
        <ol className="space-y-2">
          {data.playlist.map(
            (p, i) =>
              p.track && (
                <li key={i}>
                  <a href={appleMusicUrl(p.track.id)} target="_blank" rel="noreferrer" className="flex items-center gap-3 rounded-2xl bg-surface p-2.5">
                    <span className="w-10 text-xs text-ink-faint">{formatClock(p.startedAt)}</span>
                    <Artwork track={p.track} className="h-11 w-11" rounded="rounded-lg" />
                    <div className="min-w-0 flex-1">
                      <div className="truncate font-semibold">{p.track.title}</div>
                      <div className="truncate text-sm text-ink-dim">
                        {p.track.artist}
                        {p.proposer && <span className="text-ink-faint"> · {p.proposer.emoji} {p.proposer.pseudo}</span>}
                      </div>
                    </div>
                    <span className="text-ink-faint"></span>
                  </a>
                </li>
              ),
          )}
        </ol>
      </section>
    </main>
  );
}

function Stat({ value, label }: { value: number; label: string }) {
  return (
    <div className="rounded-2xl bg-surface px-2 py-4">
      <div className="font-display text-3xl font-bold text-gradient">{value}</div>
      <div className="mt-1 text-xs text-ink-dim">{label}</div>
    </div>
  );
}
